import React from 'react';
import Tooltip from '../common/Tooltip';
import { getTooltip } from '../../config/tooltips';

const isEmptyValue = (val) => val === '' || val === null || val === undefined;

const normalizeNumber = (val) => {
  if (isEmptyValue(val)) {
    return '';
  }
  if (typeof val === 'number') {
    return Number.isNaN(val) ? '' : val;
  }
  const parsed = Number(val);
  return Number.isNaN(parsed) ? '' : parsed;
};

const formatNumericDisplay = (val, step) => {
  if (val === '' || val === null || val === undefined) {
    return '--';
  }
  const numeric = Number(val);
  if (Number.isNaN(numeric)) {
    return '--';
  }
  if (step && Number(step) < 1) {
    const decimals = `${step}`.split('.')[1]?.length ?? 2;
    return Number(numeric.toFixed(Math.min(decimals, 4)));
  }
  return Number.isInteger(numeric) ? numeric : Number(numeric.toFixed(2));
};

const ParameterControl = ({ control, value, onChange, disabled, constraint }) => {
  const isLocked = Boolean(constraint?.locked);
  const lockReason = isLocked && constraint?.reason ? constraint.reason : null;
  const effectiveDisabled = disabled || control.editable === false || isLocked;

  // Heuristic: detect integer-only numeric controls
  const isIntegerControl = (() => {
    if (!control) return false;
    const id = String(control.id || '');
    const name = String(control.name || '');
    // Strong signals by id/name
    if (/\.target_face_count$/.test(id)) return true;
    if (/(_|\.)count$/.test(id)) return true;
    if (/\.(depth|width)$/.test(id)) return true;
    if (/count$/i.test(name)) return true;
    // If min/max/default are whole numbers and step is not fractional
    const nums = [control.min, control.max, control.defaultValue].filter((v) => typeof v === 'number');
    const allWhole = nums.length > 0 && nums.every((n) => Number.isInteger(n));
    const stepWhole = control.step == null || Number.isInteger(control.step) || control.step >= 1;
    return allWhole && stepWhole;
  })();

  const handleChange = (event) => {
    const { type, checked, value: inputValue } = event.target;
    if (type === 'number') {
      if (inputValue === '') {
        onChange(control, '');
        return;
      }
      const parsed = Number(inputValue);
      if (!Number.isNaN(parsed)) {
        onChange(control, isIntegerControl ? Math.round(parsed) : parsed);
      }
      return;
    }
    const newValue = type === 'checkbox' ? checked : inputValue;
    onChange(control, newValue);
  };

  const handleSliderChange = (event) => {
    const sliderValue = Number(event.target.value);
    if (!Number.isNaN(sliderValue)) {
      onChange(control, isIntegerControl ? Math.round(sliderValue) : sliderValue);
    }
  };

  const renderInput = () => {
    if (control.controlType === 'switch') {
      return (
        <label className="param-control__switch">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={handleChange}
            disabled={effectiveDisabled}
            title={lockReason || undefined}
          />
          <span className="param-control__switch-indicator" />
        </label>
      );
    }

    if (control.controlType === 'select') {
      return (
        <select value={value ?? ''} onChange={handleChange} disabled={effectiveDisabled} title={lockReason || undefined}>
          <option value="" disabled>
            Select an option
          </option>
          {control.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (control.controlType === 'number') {
      const hasRange = typeof control.min === 'number' || typeof control.max === 'number';
      const rangeMin = typeof control.min === 'number' ? control.min : 0;
      const rangeMax = typeof control.max === 'number' ? control.max : rangeMin + 100;
      const numericValueRaw = normalizeNumber(value);
      const numericValue = isIntegerControl && numericValueRaw !== ''
        ? Math.round(Number(numericValueRaw))
        : numericValueRaw;
      const isNullValue = control.nullable && (value === null || value === '');
      const baseValue =
        typeof numericValue === 'number'
          ? numericValue
          : typeof control.defaultValue === 'number'
            ? control.defaultValue
            : rangeMin;
      const sliderValue = isNullValue
        ? rangeMin
        : Math.min(rangeMax, Math.max(rangeMin, baseValue));
      const sliderStep = (() => {
        if (!hasRange) {
          return undefined;
        }
        if (typeof control.step === 'number' && control.step > 0) {
          // Respect explicit step, but coerce to integer if this is an integer control
          return isIntegerControl ? Math.max(1, Math.round(control.step)) : control.step;
        }
        const span = Math.abs(rangeMax - rangeMin) || 1;
        if (isIntegerControl) {
          // For large integer ranges, keep ~100 stops and ensure integer step
          return Math.max(1, Math.round(span / 100));
        }
        // Default fractional step heuristics
        const rawStep = span / 100;
        if (span <= 1) {
          return Number(Math.max(0.001, Number(rawStep.toFixed(3))));
        }
        if (span <= 10) {
          return Number(Math.max(0.01, Number(rawStep.toFixed(2))));
        }
        return 0.1;
      })();
      const nullThreshold = control.nullable
        ? rangeMin + (sliderStep || Math.max(Math.abs(rangeMax - rangeMin) / 100, 0.0001))
        : null;
      const displayValue = isNullValue
        ? 'Off'
        : (isIntegerControl
            ? (numericValue === '' ? Math.round(sliderValue) : Math.round(Number(numericValue)))
            : formatNumericDisplay(
                numericValue === '' ? sliderValue : numericValue,
                control.step ?? sliderStep
              )
          );

      return (
        <div className={`param-control__number${hasRange ? ' param-control__number--slider' : ''}`}>
          {hasRange ? (
            <>
              <input
                className="param-control__slider"
                type="range"
                min={rangeMin}
                max={rangeMax}
                step={sliderStep}
                value={isNullValue ? rangeMin : sliderValue}
                onChange={(event) => {
                  const sliderVal = Number(event.target.value);
                  if (control.nullable && nullThreshold !== null && sliderVal <= nullThreshold) {
                    onChange(control, null);
                  } else {
                    handleSliderChange(event);
                  }
                }}
                disabled={effectiveDisabled}
                title={lockReason || undefined}
              />
              <div className="param-control__scale" aria-hidden="true">
                <span className="param-control__scale-min">{rangeMin}</span>
                <span className="param-control__scale-value">{displayValue}</span>
                <span className="param-control__scale-max">{rangeMax}</span>
              </div>
            </>
          ) : (
            <input
              type="number"
              value={value ?? ''}
              onChange={handleChange}
              disabled={effectiveDisabled}
              min={control.min ?? undefined}
              max={control.max ?? undefined}
              step={isIntegerControl ? 1 : (control.step ?? undefined)}
              title={lockReason || undefined}
            />
          )}
        </div>
      );
    }

    if (control.controlType === 'static-list') {
      return (
        <div className="param-control__static">
          {Array.isArray(control.defaultValue) && control.defaultValue.length > 0 ? (
            <ul>
              {control.defaultValue.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p>No options available</p>
          )}
        </div>
      );
    }

    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={handleChange}
        disabled={effectiveDisabled}
        placeholder="Enter value"
        title={lockReason || undefined}
      />
    );
  };

  const tooltip = getTooltip(control.id);

  const labelContent = (
    <label className="param-control__label">
      <span>
        {control.label}
        {tooltip && (
          <svg className="param-control__info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9,9h0a3,3,0,0,1,6,0c0,2-3,3-3,3"/>
            <path d="M12,17h0"/>
          </svg>
        )}
      </span>
      {control.helpText && !tooltip?.description ? <small>{control.helpText}</small> : null}
    </label>
  );

  return (
    <div className={`param-control${effectiveDisabled ? ' param-control--disabled' : ''}`}>
      {tooltip ? (
        <Tooltip
          title={tooltip.title}
          description={tooltip.description}
          position="top"
        >
          {labelContent}
        </Tooltip>
      ) : (
        labelContent
      )}
      {renderInput()}
      {lockReason ? <p className="param-control__constraint">{lockReason}</p> : null}
    </div>
  );
};

export default ParameterControl;
